import React, { useState, useRef, useEffect } from 'react';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Modal, Button } from './ui';

interface IssueCreatorModalProps {
  isOpen: boolean;
  onClose: () => void;
  roomId: string;
  issueCount: number;
  onIssuesCreated: () => void;
}

const IssueCreatorModal: React.FC<IssueCreatorModalProps> = ({
  isOpen,
  onClose,
  roomId,
  issueCount,
  onIssuesCreated
}) => {
  const [issueText, setIssueText] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [issueText]);

  useEffect(() => {
    if (isOpen && textareaRef.current) {
      // Focus the textarea when modal opens
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const createIssues = async (titles: string[]) => {
    setIsCreating(true);
    try {
      const promises = titles.map((title, index) => {
        if (!title.trim()) return null;

        const issueData = {
          title: title.trim(),
          roomId,
          order: issueCount + index,
          isFinalized: false
        };

        return addDoc(collection(db, 'issues'), issueData);
      }).filter(Boolean);

      await Promise.all(promises);

      setIssueText('');
      onIssuesCreated();
      onClose();
    } catch (error) {
      // Failed to create issues
    } finally {
      setIsCreating(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      // Ctrl+Enter to submit
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleSubmit = () => {
    if (!issueText.trim()) return;

    const lines = issueText.split('\n').filter(line => line.trim());
    createIssues(lines);
  };

  const handleClose = () => {
    if (!isCreating) {
      setIssueText('');
      onClose();
    }
  };

  const lineCount = issueText.split('\n').filter(line => line.trim()).length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Issues"
      size="lg"
    >
      <div className="space-y-6">
        <div>
          <p className="text-xs text-gray-600 mb-4">
            Add one or more issues to estimate. Each line will become a separate issue.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <div className="flex items-center text-blue-800 text-xs">
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" />
              </svg>
              💡 Tip: Use Ctrl+Enter to quickly submit
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="relative">
            <textarea
              ref={textareaRef}
              value={issueText}
              onChange={(e) => setIssueText(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter issue titles...&#10;&#10;Example:&#10;User authentication system&#10;Product search functionality&#10;Shopping cart integration"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none min-h-[150px] max-h-[300px] font-mono text-xs"
              disabled={isCreating}
            />
            {lineCount > 0 && (
              <div className="absolute top-2 right-2 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-medium">
                {lineCount} {lineCount === 1 ? 'issue' : 'issues'}
              </div>
            )}
          </div>

          {/* Preview */}
          {lineCount > 1 && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-xs font-medium text-gray-700 mb-2">Preview ({lineCount} issues):</h4>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {issueText.split('\n').filter(line => line.trim()).map((line, index) => (
                  <div key={index} className="text-xs text-gray-600 flex items-center">
                    <span className="w-4 h-4 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-[10px] mr-2 flex-shrink-0">
                      {index + 1}
                    </span>
                    <span className="truncate">{line.trim()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-between items-center pt-4 border-t border-gray-200">
          <span className="text-xs text-gray-500">
            {lineCount === 0 ? 'Start typing to add issues...' :
              lineCount === 1 ? '1 issue ready' :
                `${lineCount} issues ready`}
          </span>

          <div className="flex gap-3">
            <Button
              variant="secondary"
              onClick={handleClose}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button
              variant="success"
              onClick={handleSubmit}
              disabled={!issueText.trim() || isCreating}
            >
              {isCreating ? 'Creating...' : `Add ${lineCount > 1 ? `${lineCount} Issues` : 'Issue'}`}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default IssueCreatorModal;
